import type {
  CevroMessengerConfig,
  SendMessageRequest,
  Message,
  Conversation,
  ConversationHistoryItem,
  ConversationHistoryResponse,
  ConversationMessagesResponse,
  Agent,
  LocaleStrings,
  WidgetAppearanceConfig,
} from '../types';
import { collectSessionData } from '../utils/session';
import {
  validateFile,
  sanitizeFilename,
  UploadRateLimiter,
} from '../utils/file-validation';

/**
 * API client for communicating with Cevro backend
 */
export class ApiClient {
  private config: CevroMessengerConfig;
  private accessToken: string | null = null;
  private contactId: string | null = null;
  private currentTicketId: string | null = null;
  private visitorToken: string | null = null;
  private currentAgent: Agent | null = null;
  private uploadRateLimiter: UploadRateLimiter;
  private visitorId: string | null = null;

  constructor(config: CevroMessengerConfig) {
    this.config = config;
    this.uploadRateLimiter = new UploadRateLimiter();
  }

  /**
   * Update config reference (used when config object is replaced)
   */
  updateConfig(config: CevroMessengerConfig): void {
    this.config = config;
  }

  /**
   * Set visitor token for authenticated requests
   */
  setVisitorToken(token: string): void {
    this.visitorToken = token;
  }

  /**
   * Get visitor token
   */
  getVisitorToken(): string | null {
    return this.visitorToken;
  }

  /**
   * Set access token for authenticated requests
   */
  setAccessToken(token: string): void {
    this.accessToken = token;
  }

  /**
   * Get current contact ID
   */
  getContactId(): string | null {
    return this.contactId;
  }

  /**
   * Get current ticket ID
   */
  getCurrentTicketId(): string | null {
    return this.currentTicketId;
  }

  /**
   * Set current ticket ID
   */
  setCurrentTicketId(ticketId: string): void {
    this.currentTicketId = ticketId;
  }

  /**
   * Get current agent info
   */
  getCurrentAgent(): Agent | null {
    return this.currentAgent;
  }

  /**
   * Make API request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.config.apiBase}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add workspace headers for gateway auth
    if (this.config.workspaceId) {
      headers['X-Workspace-Id'] = this.config.workspaceId;
    }
    if (this.config.brandId) {
      headers['X-Brand-Id'] = this.config.brandId;
    }
    if (this.visitorToken) {
      headers['X-Visitor-Token'] = this.visitorToken;
    }
    if (this.config.player?.playerId) {
      headers['X-Player-Id'] = this.config.player.playerId;
    }
    if (this.config.player?.playerHash) {
      headers['X-Player-Hash'] = this.config.player.playerHash;
    }
    if (this.visitorId) {
      headers['X-Visitor-Id'] = this.visitorId;
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Initialize a chat session
   * Called when chat opens to create/restore session
   */
  async initSession(
    visitorId: string | null,
    visitorContactInfo?: { firstName: string; email: string } | null,
    options?: { forceNewTicket?: boolean }
  ): Promise<{
    sessionId: string;
    contactId: string;
    ticketId: string;
    visitorId: string;
    isNewSession: boolean;
    isNewTicket: boolean;
    ticketClosed?: boolean;
    ticketClosedReason?: string;
    agent?: Agent;
    typingConfig?: {
      onDurationMs: number;
      offDurationMs: number;
    };
    messages?: Message[];
    hasMoreMessages?: boolean;
    cursor?: string;
    locale?: Partial<LocaleStrings>;
    requireContactInfo?: boolean;
    missingFields?: ('firstName' | 'email')[];
  }> {
    const session = collectSessionData();

    const payload: any = {
      visitorId,
      session,
      brandId: this.config.brandId,
      // Use visitor contact info if provided, otherwise fall back to player config
      firstName: visitorContactInfo?.firstName || this.config.player?.firstName,
      lastName: this.config.player?.lastName,
      email: (visitorContactInfo?.email || this.config.player?.email) || undefined,
      phone: this.config.player?.phone,
      customAttributes: this.config.player?.customAttributes,
      forceNewTicket: options?.forceNewTicket,
    };

    // Add player data for authenticated users (for merge logic)
    if (this.config.player?.playerId) {
      payload.playerId = this.config.player.playerId;
      payload.playerHash = this.config.player.playerHash;
    }

    const result = await this.request<any>('/api/v1/sdk/session', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: visitorId ? { 'X-Visitor-Id': visitorId } : undefined,
    });

    // If contact info is required, return early without storing session
    if (result.requireContactInfo && result.missingFields?.length > 0) {
      return {
        sessionId: '',
        contactId: '',
        ticketId: '',
        visitorId: result.visitorId || '',
        isNewSession: false,
        isNewTicket: false,
        requireContactInfo: result.requireContactInfo,
        missingFields: result.missingFields,
      };
    }

    // Store contact, ticket IDs and agent info
    this.contactId = result.contactId;
    this.currentTicketId = result.ticketId;
    if (result.agent) {
      this.currentAgent = result.agent;
    }

    return {
      sessionId: result.sessionId,
      contactId: result.contactId,
      ticketId: result.ticketId,
      visitorId: result.visitorId,
      isNewSession: result.isNewSession,
      isNewTicket: result.isNewTicket,
      ticketClosed: result.ticketClosed,
      ticketClosedReason: result.ticketClosedReason,
      agent: result.agent,
      typingConfig: result.typingConfig,
      messages: result.messages?.map((msg: any) => this.normalizeMessage(msg)),
      hasMoreMessages: result.hasMoreMessages,
      cursor: result.cursor,
      locale: result.locale,
      requireContactInfo: result.requireContactInfo,
    };
  }

  /**
   * Send a message to an existing conversation
   */
  async sendMessage(message: string, assetId?: string): Promise<Message> {
    if (!this.contactId || !this.currentTicketId) {
      throw new Error('No active session. Call initSession first.');
    }

    const payload: SendMessageRequest = {
      contactId: this.contactId,
      ticketId: this.currentTicketId,
      message,
      assetId,
    };

    const result = await this.request<any>('/api/v1/sdk/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    // Server returns {success: true, message: {...}}, extract the message
    return this.normalizeMessage(result.message || result);
  }

  /**
   * Get messages for the current ticket (for authenticated users)
   */
  async getMessages(ticketId?: string): Promise<Message[]> {
    const targetTicketId = ticketId || this.currentTicketId;
    if (!targetTicketId) {
      throw new Error('No ticket ID available');
    }

    const result = await this.request<any>(
      `/api/v1/messages/my-tickets?supportTicketId=${targetTicketId}`
    );

    const messages = result.results || result.messages || result || [];
    return messages.map((msg: any) => this.normalizeMessage(msg));
  }

  /**
   * Get user's support tickets (for authenticated users)
   */
  async getMyTickets(): Promise<Conversation[]> {
    const result = await this.request<any>('/api/v1/messages/my-tickets');

    const tickets = result.results || result.tickets || result || [];
    return tickets.map((ticket: any) => this.normalizeConversation(ticket));
  }

  /**
   * Upload a file using presigned S3 URL for better performance.
   * The file is uploaded directly to S3, bypassing the backend.
   *
   * Security features:
   * - File size validation (client-side)
   * - MIME type whitelist validation
   * - Blocked extension check
   * - Rate limiting (uploads per minute)
   * - Filename sanitization
   *
   * @param file - The file to upload
   * @param onProgress - Optional callback for upload progress (0-100)
   * @returns The asset ID to use when sending messages
   * @throws Error if validation fails or rate limited
   */
  async uploadFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    // Step 0: Rate limiting check
    if (!this.uploadRateLimiter.canUpload()) {
      const waitTime = Math.ceil(this.uploadRateLimiter.getTimeUntilNextUpload() / 1000);
      throw new Error(`Too many uploads. Please wait ${waitTime} seconds before uploading again.`);
    }

    // Step 1: Validate file (size, type, extension)
    const validation = validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error || 'File validation failed');
    }

    // Step 2: Sanitize filename
    const sanitizedFilename = sanitizeFilename(file.name);

    // Step 3: Record upload attempt for rate limiting
    this.uploadRateLimiter.recordUpload();

    // Step 4: Get presigned URL from backend
    const { uploadUrl, assetId } = await this.request<{
      uploadUrl: string;
      assetId: string;
      expiresIn: number;
    }>('/api/v1/sdk/upload-url', {
      method: 'POST',
      body: JSON.stringify({
        filename: sanitizedFilename,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      }),
    });

    // Step 5: Upload file directly to S3
    await this.uploadToS3(uploadUrl, file, onProgress);

    return assetId;
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  private async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
  }

  /**
   * Normalize message object from API response
   */
  private normalizeMessage(data: any): Message {
    return {
      id: data.objectId || data.id,
      body: data.body || '',
      inbound: data.inbound || false,
      outbound: data.outbound || !data.inbound,
      createdAt: data.createdAt || new Date().toISOString(),
      type: data.type || 'text',
      mediaUrl: data.mediaUrl,
      mediaType: data.mediaType,
      status: data.status || 'sent',
      richContent: data.richContent,
    };
  }

  /**
   * Normalize conversation object from API response
   */
  private normalizeConversation(data: any): Conversation {
    return {
      id: data.objectId || data.id,
      conversationId: data.conversationId || data.objectId || data.id,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      status: data.resolved ? 'resolved' : data.escalated ? 'escalated' : 'open',
      unreadCount: data.unreadCount || 0,
      lastMessage: data.latestMessage ? this.normalizeMessage(data.latestMessage) : undefined,
    };
  }

  /**
   * Get Centrifugo connection token for real-time messaging
   *
   * For authenticated players: uses playerHash for identity verification
   * For visitors: uses server-signed visitorToken for security
   *
   * @param visitorToken - Server-signed visitor token (optional, server generates if not provided)
   */
  async getConnectionToken(visitorToken?: string | null): Promise<{
    token: string;
    visitorId: string;
    visitorToken: string;
    expiresAt: number;
  }> {
    const payload: any = {
      workspaceId: this.config.workspaceId,
      brandId: this.config.brandId,
    };

    if (visitorToken) {
      payload.visitorToken = visitorToken;
    }

    // Add player data for authenticated users
    if (this.config.player?.playerId) {
      payload.playerId = this.config.player.playerId;
      payload.playerHash = this.config.player.playerHash;
      payload.email = this.config.player.email || undefined;
      payload.firstName = this.config.player.firstName;
    }

    const result = await this.request<{
      token: string;
      visitorId: string;
      visitorToken: string;
      expiresAt: number;
    }>('/api/v1/sdk/token', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return result;
  }

  /**
   * Refresh Centrifugo token before expiration
   */
  async refreshConnectionToken(visitorToken?: string | null): Promise<{
    token: string;
    visitorToken: string;
    expiresAt: number;
  }> {
    return this.getConnectionToken(visitorToken);
  }

  /**
   * Submit CSAT rating for a closed conversation
   */
  async submitCSAT(
    ticketId: string,
    contactId: string,
    rating: number,
    feedback?: string
  ): Promise<void> {
    await this.request('/api/v1/sdk/csat', {
      method: 'POST',
      body: JSON.stringify({
        ticketId,
        contactId,
        rating,
        feedback,
      }),
    });
  }

  /**
   * Get past conversations for the current contact
   */
  async getConversations(cursor?: string): Promise<{
    conversations: ConversationHistoryItem[];
    hasMore: boolean;
    cursor?: string;
  }> {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);

    const url = params.toString()
      ? `/api/v1/sdk/conversations?${params.toString()}`
      : '/api/v1/sdk/conversations';

    const result = await this.request<ConversationHistoryResponse>(url);

    return {
      conversations: result.conversations || [],
      hasMore: result.hasMore || false,
      cursor: result.cursor,
    };
  }

  /**
   * Get messages for a past conversation (paginated)
   */
  async getConversationMessages(ticketId: string, cursor?: string): Promise<{
    messages: Message[];
    hasMore: boolean;
    cursor?: string;
  }> {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);

    const base = `/api/v1/sdk/conversations/${ticketId}/messages`;
    const url = params.toString() ? `${base}?${params.toString()}` : base;

    const result = await this.request<ConversationMessagesResponse>(url);

    return {
      messages: (result.messages || []).map((msg: any) => this.normalizeMessage(msg)),
      hasMore: result.hasMore || false,
      cursor: result.cursor,
    };
  }

  /**
   * Set visitor ID (sent as X-Visitor-Id header on all requests)
   */
  setVisitorId(id: string): void {
    this.visitorId = id;
  }

  async getWidgetConfig(): Promise<WidgetAppearanceConfig> {
    try {
      const result = await this.request<WidgetAppearanceConfig>('/api/v1/sdk/widget-config', {
        method: 'GET',
      });
      return result;
    } catch (error) {
      // Return empty config on error - widget will use defaults
      return {};
    }
  }

  /**
   * Clear stored data (on shutdown)
   */
  clear(): void {
    this.accessToken = null;
    this.contactId = null;
    this.currentTicketId = null;
    this.visitorToken = null;
    this.visitorId = null;
    this.currentAgent = null;
  }
}
